import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { addDoc, collection, deleteDoc, doc, onSnapshot, query, runTransaction, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth, usePermissions } from '../AuthContext';
import DialogErrorBoundary from '../components/DialogErrorBoundary';
import {
  aggregateMaterialRequirements,
  materialProcurementRequirementRows,
  SteelQuoteDialog,
  materialOrderCatalog,
  type MaterialProcurementOrder,
  type MaterialQuote,
  type MaterialRateItem,
  type MaterialBoxPacking,
  type MaterialAssembly,
  type ConcreteGrade,
  type ProjectEstimateScopeItem,
  type UnitTypeBuildingAllocation,
  type UnitTypeMaster,
} from './OfficeEstimationPage';
import BillsScreen, { type BillView } from '../components/BillsScreen';
import type { DirectBillPayload, DirectMode, DirectReason } from '../office/directBill';
import { type BillPayload, type OrderRow } from '../components/MaterialReceivingDialogs';
import { createMaterialItem, masterRef, projectRatesRef, setProjectRates, updateMaterialItem } from '../office/materialLibraryStore';
import { addVarietyPatch, buildMaterialPayload, type NewMaterialInput } from '../office/newMaterial';
import { effectiveQuotes, planQuoteWrite } from '../office/materialLibrary';
import { useMaterialLibrary } from '../office/useMaterialLibrary';
import { ensureVendors, saveCartingAgents, useVendorCartingAgents, useVendors } from '../office/vendorStore';
import OrdersScreen, { type OrdersScreenOrder } from '../components/OrdersScreen';
import ReceivingScreen from '../components/ReceivingScreen';
import StockScreen from '../components/StockScreen';
import type { StockIssue } from '../office/materialStock';
import { resizeImage } from '../office/imageResize';
import {
  deliveryReceiptFields, findOpenPoLines, isReceiptBilled, libraryPicks, liveOrderLabel, needsVendorQuote, normVendor, orderGroup, orderName, orderPendingQty, orderReceivedQty, orderTitle, pickForOrder, planOrderPriceEntry, quotesWithBillRevision, sameVendorName, vendorOptionsForItem,
  type DeliveryLine, type DeliveryMeta, type MaterialPick,
} from '../office/materialReceiving';

const BORDER = '#e8e8e8';
const GOLD_D = '#7a5525';
const MUTED = '#6b6b6b';
const FAINT = '#938b7d';
const DARK = '#1a1a1a';
const WARN = '#c07a12';
const GREEN = '#3c6b4b';
const BLUE = '#3a6ea5';
const fmtQty = (value: number) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const toNumber = (value: unknown) => { const n = parseFloat(String(value)); return Number.isFinite(n) ? n : 0; };
const sameText = (a?: string, b?: string) => !!String(a || '').trim() && String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
const fmtDate = (date: string) => {
  const value = new Date(`${date}T00:00:00`);
  return Number.isNaN(value.getTime()) ? date : value.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

type Receipt = {
  id: string; date: string; qty: number; reference?: string; notes?: string; documentType?: 'challan' | 'bill';
  boxes?: number; piecesPerBox?: number; areaPerPiece?: number;
  billDetailsPending?: boolean; gateBill?: boolean; billId?: string; billNumber?: string; billDate?: string; photoUrl?: string; photoPath?: string;
  cartingAgent?: string;
};
type Allocation = { buildingId: string; buildingLabel: string; qty: number };
type Order = {
  id: string; materialLabel: string; unit: string; vendor?: string; poNumber?: string; orderDate: string; expectedDate?: string; orderQty: number;
  materialId?: string; quoteId?: string; quoteOptionId?: string; rateSnapshot?: number; brand?: string; code?: string; notes?: string; scopeType?: string; scopeLabel?: string; receipts?: Receipt[]; buildingAllocations?: Allocation[];
  sourceType?: 'purchaseOrder' | 'directReceipt'; unplannedReviewed?: boolean; closedShort?: boolean; closedShortNote?: string;
  needsMapping?: boolean; newVendor?: boolean; vendorQuotePending?: boolean; unplannedRejected?: boolean; unplannedRejectNote?: string;
};
type EnrichedOrder = Order & { received: number; pending: number; over: number; displayTitle?: string; displayGroup?: string };
type Bill = {
  id: string; invoiceNumber: string; invoiceDate: string; vendor: string; freight: number; roundOff: number; invoiceTotal: number;
  calculatedTotal: number; mismatchReason?: string; notes?: string; libraryLinesUpdated?: number;
  sourceType?: 'direct'; directReason?: DirectReason; directMode?: DirectMode; linkedReceipts?: Array<{ orderId: string; receiptId: string }>;
  lines: Array<{ orderId: string; receiptId?: string; materialId?: string; quoteOptionId?: string; materialLabel: string; unit: string; qty: number; rate: number; amount: number; enteredRate?: number; enteredUnit?: string; unmapped?: boolean }>;
};
type TabKey = 'orders' | 'receiving' | 'bills' | 'stock';
type ReceiptEdit = { order: EnrichedOrder; receipt: Receipt };

export default function OfficeMaterialOrdersPage() {
  const { currentProjectId, roleId } = useAuth();
  const { canAction } = usePermissions();
  const isAdmin = ['super-admin', 'admin'].includes(String(roleId || ''));
  const canManage = isAdmin || canAction('materialOrders', 'manage');
  const canReceive = canManage || canAction('materialOrders', 'receive');
  const canBill = isAdmin || canAction('materialOrders', 'bill');
  // Bills with no order or delivery behind them, and linking them to a delivery later, need their own permission on top of billing.
  const canDirectBill = isAdmin || (canBill && canAction('materialOrders', 'billDirect'));
  const canViewOrders = canManage || canAction('materialOrders', 'view') || (!canReceive && !canBill);
  const availableTabs = useMemo<TabKey[]>(() => [...(canViewOrders ? ['orders' as const] : []), ...(canReceive ? ['receiving' as const] : []), ...(canBill ? ['bills' as const] : []), ...(isAdmin ? ['stock' as const] : [])], [canViewOrders, canReceive, canBill, isAdmin]);
  const [tabChoice, setTabChoice] = useState<TabKey | null>(null);
  const [recentReceiptsShown, setRecentReceiptsShown] = useState(8);
  const [recentReceiptQuery, setRecentReceiptQuery] = useState('');
  const tab: TabKey = tabChoice && availableTabs.includes(tabChoice) ? tabChoice : (canManage ? 'orders' : canReceive ? 'receiving' : canBill ? 'bills' : 'orders');

  const [orders, setOrders] = useState<Order[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  // Materials are shared by every project; quotes and selections belong to this project.
  const library = useMaterialLibrary(currentProjectId);
  const knownVendors = useVendors();
  const cartingAgentsByVendor = useVendorCartingAgents();
  const materialItems = library.items;
  const [assemblies, setAssemblies] = useState<MaterialAssembly[]>([]);
  const [concreteGrades, setConcreteGrades] = useState<ConcreteGrade[]>([]);
  const [projectScopeItems, setProjectScopeItems] = useState<ProjectEstimateScopeItem[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitTypeMaster[]>([]);
  const [buildingAllocations, setBuildingAllocations] = useState<Record<string, UnitTypeBuildingAllocation[]>>({});
  const [resolve, setResolve] = useState<{ order: EnrichedOrder; mode: 'link' | 'map' } | null>(null);
  const [editingOrder, setEditingOrder] = useState<EnrichedOrder | null>(null);
  const [editingReceipt, setEditingReceipt] = useState<ReceiptEdit | null>(null);
  const [receivingKey, setReceivingKey] = useState(0);
  const [ordersKey, setOrdersKey] = useState(0);
  // Asking purchasing to add the delivering vendor's quote to the rate library.
  const [quoteFlow, setQuoteFlow] = useState<{ order: EnrichedOrder; item: MaterialRateItem; stage: 'ask' | 'form' } | null>(null);
  const [billsKey, setBillsKey] = useState(0);
  const [error, setError] = useState('');
  const [stockIssues, setStockIssues] = useState<StockIssue[]>([]);

  useEffect(() => {
    if (!currentProjectId) { setOrders([]); return; }
    return onSnapshot(query(collection(db, 'projects', currentProjectId, 'materialProcurementOrders')), (snapshot) => {
      setOrders(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<Order, 'id'>) } as Order)).sort((a, b) => String(b.orderDate || '').localeCompare(String(a.orderDate || ''))));
    }, () => setError('Could not load material orders.'));
  }, [currentProjectId]);

  // Bills hold rates and totals, so only the billing role (and admin) may read them.
  useEffect(() => {
    if (!currentProjectId || !canBill) { setBills([]); return; }
    return onSnapshot(query(collection(db, 'projects', currentProjectId, 'materialVendorBills')), (snapshot) => {
      setBills(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<Bill, 'id'>) } as Bill)).sort((a, b) => String(b.invoiceDate || '').localeCompare(String(a.invoiceDate || ''))));
    }, () => setError('Could not load vendor bills.'));
  }, [currentProjectId, canBill]);

  useEffect(() => {
    if (!currentProjectId || !isAdmin) { setStockIssues([]); return; }
    return onSnapshot(query(collection(db, 'projects', currentProjectId, 'materialStockIssues')), (snapshot) => {
      setStockIssues(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<StockIssue, 'id'>) } as StockIssue)));
    }, () => setError('Could not load the stock ledger.'));
  }, [currentProjectId, isAdmin]);

  useEffect(() => {
    if (!currentProjectId) {
      setAssemblies([]);
      setConcreteGrades([]);
      setProjectScopeItems([]); setUnitTypes([]); setBuildingAllocations({});
      return;
    }
    const subscriptions = [
      onSnapshot(doc(db, 'officeSettings', 'materialAssemblyLibrary'), (snapshot) => setAssemblies((snapshot.data()?.assemblies || []) as MaterialAssembly[]), () => setAssemblies([])),
      onSnapshot(doc(db, 'officeSettings', 'concreteGrades'), (snapshot) => setConcreteGrades((snapshot.data()?.grades || []) as ConcreteGrade[]), () => setConcreteGrades([])),
      onSnapshot(doc(db, 'officeSettings', `projectEstimateScope_${currentProjectId}`), (snapshot) => setProjectScopeItems((snapshot.data()?.items || []) as ProjectEstimateScopeItem[]), () => setProjectScopeItems([])),
      onSnapshot(doc(db, 'officeSettings', 'unitTypeMasterLibrary'), (snapshot) => setUnitTypes((snapshot.data()?.unitTypes || []) as UnitTypeMaster[]), () => setUnitTypes([])),
      onSnapshot(doc(db, 'officeSettings', `unitTypeBuildingAllocations_${currentProjectId}`), (snapshot) => setBuildingAllocations((snapshot.data()?.allocations || {}) as Record<string, UnitTypeBuildingAllocation[]>), () => setBuildingAllocations({})),
    ];
    return () => subscriptions.forEach((unsubscribe) => unsubscribe());
  }, [currentProjectId]);

  const projectUnitTypes = useMemo(() => unitTypes.map((unit) => ({
    ...unit,
    buildingAllocations: buildingAllocations[unit.id] || unit.buildingAllocations || [],
  })), [unitTypes, buildingAllocations]);
  const orderRequirements = useMemo(() => {
    const leaves = materialProcurementRequirementRows(projectUnitTypes, materialItems, assemblies, projectScopeItems);
    return [
      ...aggregateMaterialRequirements(leaves, 'project'),
      ...aggregateMaterialRequirements(leaves, 'building'),
    ];
  }, [projectUnitTypes, materialItems, assemblies, projectScopeItems]);

  // Names come from the library, so renaming a group, material or variety shows on old orders and receipts too.
  const labelPicks = useMemo(() => libraryPicks(materialItems), [materialItems]);
  const catalogLabels = useMemo(() => new Map(materialOrderCatalog(materialItems, []).map((row) => [`${row.materialId}|${row.quoteOptionId || ''}`, row.label])), [materialItems]);
  const enriched = useMemo<EnrichedOrder[]>(() => orders.map((order) => {
    const received = orderReceivedQty({ receipts: order.receipts || [] });
    return {
      ...order, received,
      materialLabel: liveOrderLabel(order as any, labelPicks, catalogLabels),
      ...(() => { const name = orderName({ ...(order as any), materialLabel: liveOrderLabel(order as any, labelPicks, catalogLabels) }, materialItems); return { displayTitle: name.title, displayGroup: name.group }; })(),
      pending: orderPendingQty({ receipts: order.receipts || [], orderQty: order.orderQty, closedShort: order.closedShort }),
      over: Math.max(0, received - Number(order.orderQty || 0)),
    };
  }), [orders, labelPicks, catalogLabels, materialItems]);
  const openOrders = useMemo(() => enriched.filter((order) => order.pending > 0), [enriched]);
  const unplannedOrders = useMemo(() => enriched.filter((order) => order.sourceType === 'directReceipt' && !order.unplannedReviewed && !order.unplannedRejected), [enriched]);
  const vendorQuoteWaiting = useMemo(() => {
    const groups = new Map<string, { orders: EnrichedOrder[]; item: MaterialRateItem }>();
    for (const order of enriched) {
      if (!order.vendorQuotePending || order.unplannedRejected) continue;
      const item = materialItems.find((candidate) => candidate.id === order.materialId);
      if (!item) continue;
      const key = `${item.id}|${normVendor(order.vendor).replace(/[^a-z0-9]/g, '')}`;
      const group = groups.get(key) || { orders: [], item };
      group.orders.push(order);
      groups.set(key, group);
    }
    return Array.from(groups.values());
  }, [enriched, materialItems]);
  const rejectedOrders = useMemo(() => enriched.filter((order) => order.unplannedRejected), [enriched]);
  const unbilledRows = useMemo(() => enriched.filter((order) => !order.unplannedRejected).flatMap((order) =>
    (order.receipts || []).filter((receipt) => !isReceiptBilled(receipt)).map((receipt) => ({ key: `${order.id}__${receipt.id}`, order, receipt }))), [enriched]);

  const orderRef = (id: string) => doc(db, 'projects', currentProjectId as string, 'materialProcurementOrders', id);

  // Purchasing: every line of a checkout is written together, so a failed save never leaves half a PO.
  const placeOrders = async (drafts: Array<Omit<MaterialProcurementOrder, 'id'>>) => {
    if (!currentProjectId) throw new Error('Select a project first.');
    const batch = writeBatch(db);
    drafts.forEach((draft) => batch.set(doc(collection(db, 'projects', currentProjectId, 'materialProcurementOrders')), { ...draft, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
    await batch.commit();
    ensureVendors(currentProjectId, knownVendors, drafts.map((draft) => draft.vendor || '')).catch(() => undefined);
  };

  // Site: goods received against PO lines. There is no quantity cap (deliveries often differ from the
  // order), and it runs in a transaction so simultaneous receipts are never lost.
  const saveDeliveryReceipts = async (lines: DeliveryLine[], meta: DeliveryMeta) => {
    if (!currentProjectId) return;
    await runTransaction(db, async (tx) => {
      const refs = lines.map((line) => orderRef(line.orderId));
      const snaps = await Promise.all(refs.map((ref) => tx.get(ref)));
      snaps.forEach((snap, index) => {
        if (!snap.exists()) throw new Error('A purchase order line was removed. Close this window and try again.');
        const receipts: Receipt[] = (snap.data() as any).receipts || [];
        tx.update(refs[index], {
          receipts: [...receipts, {
            id: `receipt_${Date.now()}_${index}`,
            date: meta.date,
            qty: lines[index].qty,
            ...(lines[index].boxes ? { boxes: lines[index].boxes, piecesPerBox: lines[index].piecesPerBox, areaPerPiece: lines[index].areaPerPiece } : {}),
            ...deliveryReceiptFields(meta),
          }],
          updatedAt: serverTimestamp(),
        });
      });
    });
  };

  const saveDirectReceipt = async (directOrder: Omit<MaterialProcurementOrder, 'id'>) => {
    if (!currentProjectId) return;
    await addDoc(collection(db, 'projects', currentProjectId, 'materialProcurementOrders'), {
      ...directOrder,
      sourceType: 'directReceipt',
      unplannedReviewed: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    ensureVendors(currentProjectId, knownVendors, [directOrder.vendor || '']).catch(() => undefined);
  };

  const saveStockIssues = async (issues: Omit<StockIssue, 'id'>[]) => {
    if (!currentProjectId || !issues.length) return;
    const batch = writeBatch(db);
    issues.forEach((issue) => batch.set(doc(collection(db, 'projects', currentProjectId, 'materialStockIssues')), { ...issue, recordedAt: serverTimestamp() }));
    await batch.commit();
  };
  const deleteStockIssue = (issue: StockIssue) => {
    if (!currentProjectId) return;
    const siblings = issue.batchId ? stockIssues.filter((candidate) => candidate.batchId === issue.batchId && candidate.id !== issue.id) : [];
    const prompt = siblings.length
      ? `Remove ${fmtQty(issue.qty)} ${issue.unit} ${issue.materialLabel}, plus ${siblings.length} related concrete-grade ${siblings.length === 1 ? 'entry' : 'entries'} (${siblings.map((s) => s.materialLabel).join(', ')}) recorded alongside it?`
      : `Remove the issue of ${fmtQty(issue.qty)} ${issue.unit} ${issue.materialLabel}?`;
    if (!window.confirm(prompt)) return;
    const projectId = currentProjectId;
    if (!siblings.length) {
      deleteDoc(doc(db, 'projects', projectId, 'materialStockIssues', issue.id)).catch(() => setError('Could not remove that issue.'));
      return;
    }
    const batch = writeBatch(db);
    [issue, ...siblings].forEach((one) => batch.delete(doc(db, 'projects', projectId, 'materialStockIssues', one.id)));
    batch.commit().catch(() => setError('Could not remove that issue.'));
  };

  // Accounts: attach a vendor bill to received-but-unbilled receipts. Bill, receipts and any
  // rate-library revisions are written in one transaction.
  const saveBill = async (bill: BillPayload) => {
    if (!currentProjectId) return;
    if (bill.libraryUpdates.length && library.mode !== 'shared') throw new Error('The rate library is being moved to the shared library. Untick "update the rate library" or try again after an admin finishes.');
    const projectId = currentProjectId;
    const billRef = doc(collection(db, 'projects', projectId, 'materialVendorBills'));
    const orderIds = Array.from(new Set(bill.lines.map((line) => line.orderId).filter(Boolean)));
    await runTransaction(db, async (tx) => {
      const orderSnaps = await Promise.all(orderIds.map((id) => tx.get(orderRef(id))));
      const ordersById = new Map(orderSnaps.map((snap) => [snap.id, snap]));
      const updates = bill.libraryUpdates.map((update) => ({ update, line: bill.lines.find((line) => line.orderId === update.orderId) })).filter((entry) => entry.line?.materialId);
      const itemIds = Array.from(new Set(updates.map((entry) => entry.line!.materialId as string)));
      // Quotes are shared by every project, so a rate updated from this bill applies to all of them.
      const masterSnaps = await Promise.all(itemIds.map((id) => tx.get(masterRef(id))));
      const rateSnaps = await Promise.all(itemIds.map((id) => tx.get(projectRatesRef(projectId, id))));
      const itemsById = new Map<string, MaterialRateItem>();
      itemIds.forEach((id, index) => {
        const base = materialItems.find((candidate) => candidate.id === id);
        if (base) itemsById.set(id, { ...base, quotes: effectiveQuotes(masterSnaps[index].data() as any, rateSnaps[index].data() as any) as MaterialRateItem['quotes'] });
      });

      for (const orderId of orderIds) {
        const snap = ordersById.get(orderId);
        if (!snap?.exists()) throw new Error('A purchase order line was removed. Close this window and try again.');
        const receipts: Receipt[] = ((snap.data() as any).receipts || []).map((receipt: Receipt) => ({ ...receipt }));
        for (const line of bill.lines.filter((entry) => entry.orderId === orderId)) {
          const receipt = receipts.find((entry) => entry.id === line.receiptId);
          if (!receipt || isReceiptBilled(receipt)) throw new Error(`${line.materialLabel}: this delivery was already billed or removed. Close this window and reopen it.`);
          receipt.billId = billRef.id; receipt.billNumber = bill.invoiceNumber; receipt.billDate = bill.invoiceDate; receipt.billDetailsPending = false;
        }
        tx.update(orderRef(orderId), { receipts, updatedAt: serverTimestamp() });
      }

      let libraryLinesUpdated = 0;
      for (const { update, line } of updates) {
        const item = itemsById.get(line!.materialId as string);
        const orderData = ordersById.get(update.orderId)?.data() as any;
        if (!item || !orderData) continue;
        const quotes = quotesWithBillRevision(item, { ...orderData, id: update.orderId } as MaterialProcurementOrder, update.ratePerOrderUnit, bill.invoiceDate, bill.invoiceNumber);
        if (!quotes) continue;
        itemsById.set(item.id, { ...item, quotes });
        libraryLinesUpdated += 1;
      }
      itemIds.forEach((itemId, index) => {
        const item = itemsById.get(itemId);
        if (!item || !libraryLinesUpdated || !masterSnaps[index].exists()) return;
        const plan = planQuoteWrite(item.quotes || [], masterSnaps[index].data() as any, rateSnaps[index].data() as any);
        tx.update(masterRef(itemId), { quotes: plan.quotes, updatedAt: serverTimestamp() });
        tx.set(projectRatesRef(projectId, itemId), { quoteOverrides: plan.overrides, updatedAt: serverTimestamp() }, { merge: true });
      });

      const { libraryUpdates: _libraryUpdates, ...billFields } = bill;
      tx.set(billRef, {
        ...billFields,
        lines: bill.lines.map((line) => ({ ...line, materialId: line.materialId || '', quoteId: line.quoteId || '', quoteOptionId: line.quoteOptionId || '' })),
        ratesIncludeGst: true,
        libraryLinesUpdated,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
  };

  // A bill with no order or recorded delivery behind it. It is flagged so it can be found and linked later.
  const saveDirectBill = async (bill: DirectBillPayload) => {
    if (!currentProjectId) throw new Error('Select a project first.');
    if (!canDirectBill) throw new Error('You do not have permission to add a bill without a delivery.');
    await addDoc(collection(db, 'projects', currentProjectId, 'materialVendorBills'), {
      ...bill, ratesIncludeGst: true, libraryLinesUpdated: 0, linkedReceipts: [], createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    ensureVendors(currentProjectId, knownVendors, [bill.vendor]).catch(() => undefined);
  };

  // Ties a bill entered without a delivery to deliveries recorded later, so they show as billed and cost is not counted twice.
  const linkBill = async (bill: BillView, keys: string[]) => {
    if (!currentProjectId) throw new Error('Select a project first.');
    if (!canDirectBill) throw new Error('You do not have permission to link a bill to a delivery.');
    const projectId = currentProjectId;
    const wanted = keys.map((key) => { const [orderId, receiptId] = key.split('__'); return { orderId, receiptId }; });
    const orderIds = Array.from(new Set(wanted.map((entry) => entry.orderId)));
    await runTransaction(db, async (tx) => {
      const billRef = doc(db, 'projects', projectId, 'materialVendorBills', bill.id);
      const [billSnap, ...orderSnaps] = await Promise.all([tx.get(billRef), ...orderIds.map((id) => tx.get(orderRef(id)))]);
      if (!billSnap.exists()) throw new Error('This bill no longer exists.');
      orderSnaps.forEach((snap) => {
        if (!snap.exists()) throw new Error('A delivery was removed. Close this and try again.');
        const receipts: Receipt[] = ((snap.data() as any).receipts || []).map((receipt: Receipt) => {
          if (!wanted.some((entry) => entry.orderId === snap.id && entry.receiptId === receipt.id)) return receipt;
          if (isReceiptBilled(receipt)) throw new Error('A delivery you picked already has a bill. Close this and try again.');
          return { ...receipt, billId: bill.id, billNumber: bill.invoiceNumber, billDate: bill.invoiceDate, billDetailsPending: false };
        });
        tx.update(snap.ref, { receipts, updatedAt: serverTimestamp() });
      });
      const existing: Array<{ orderId: string; receiptId: string }> = (billSnap.data() as any).linkedReceipts || [];
      tx.update(billRef, { linkedReceipts: [...existing, ...wanted.filter((entry) => !existing.some((have) => have.orderId === entry.orderId && have.receiptId === entry.receiptId))], updatedAt: serverTimestamp() });
    });
  };

  // Matches a bill line typed as a material (unmapped) to a library material, so it counts toward that
  // material's actuals from here on. Same permission as billDirect: whoever typed the name fixes it.
  const matchBillLine = async (bill: BillView, lineIndex: number, materialId: string, materialLabel: string, unit: string, quoteOptionId?: string) => {
    if (!currentProjectId) throw new Error('Select a project first.');
    if (!canDirectBill) throw new Error('You do not have permission to match a bill line to a material.');
    const projectId = currentProjectId;
    const billRef = doc(db, 'projects', projectId, 'materialVendorBills', bill.id);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(billRef);
      if (!snap.exists()) throw new Error('This bill no longer exists.');
      const lines = ((snap.data() as any).lines || []).map((line: any, index: number) => index === lineIndex
        ? { ...line, materialId, materialLabel, unit, quoteOptionId: quoteOptionId || '', unmapped: false }
        : line);
      tx.update(billRef, { lines, updatedAt: serverTimestamp() });
    });
  };

  // Voiding a bill unlinks its receipts so they return to "waiting for a bill". Rate-library
  // revisions written from the bill are not reversed.
  const voidBill = async (bill: Bill) => {
    if (!currentProjectId) return;
    const linked = bill.linkedReceipts || [];
    const message = bill.sourceType === 'direct'
      ? `Void bill ${bill.invoiceNumber}? It is removed${linked.length ? `, and its ${linked.length} linked deliver${linked.length === 1 ? 'y goes' : 'ies go'} back to "waiting for a bill"` : ''}.`
      : `Void bill ${bill.invoiceNumber}? Its ${bill.lines.length} line(s) go back to "waiting for a bill". Any rate-library update made from this bill stays in the rate history.`;
    if (!window.confirm(message)) return;
    const projectId = currentProjectId;
    try {
      await runTransaction(db, async (tx) => {
        // Lines of a bill without a delivery have no order; linked deliveries are on `linkedReceipts`.
        const orderIds = Array.from(new Set([...bill.lines.map((line) => line.orderId), ...linked.map((entry) => entry.orderId)].filter(Boolean)));
        const snaps = await Promise.all(orderIds.map((id) => tx.get(orderRef(id))));
        snaps.forEach((snap) => {
          if (!snap.exists()) return;
          const receipts: Receipt[] = ((snap.data() as any).receipts || []).map((receipt: Receipt) => {
            if (receipt.billId !== bill.id) return receipt;
            const { billId: _billId, billDate: _billDate, billNumber, billDetailsPending: _pending, ...rest } = receipt;
            return receipt.gateBill ? { ...rest, billNumber, billDetailsPending: true } : rest;
          });
          tx.update(snap.ref, { receipts, updatedAt: serverTimestamp() });
        });
        tx.delete(doc(db, 'projects', projectId, 'materialVendorBills', bill.id));
      });
    } catch (err: any) {
      setError(err?.message || 'Could not void the bill.');
    }
  };

  const closeShort = async (order: EnrichedOrder) => {
    const message = order.received > 0
      ? `Close "${orderTitle(order)}" short? The ${fmtQty(order.pending)} ${order.unit} still pending will no longer be expected.`
      : `Cancel "${orderTitle(order)}"? Nothing has been received, so the whole ${fmtQty(order.pending)} ${order.unit} will no longer be expected.`;
    if (!window.confirm(message)) return;
    try {
      await updateDoc(orderRef(order.id), { closedShort: true, closedShortNote: `${fmtQty(order.pending)} ${order.unit} not delivered`, updatedAt: serverTimestamp() });
    } catch (err: any) { setError(err?.message || 'Could not close the order line.'); }
  };
  const reopenOrder = async (order: EnrichedOrder) => {
    try { await updateDoc(orderRef(order.id), { closedShort: false, closedShortNote: '', updatedAt: serverTimestamp() }); }
    catch (err: any) { setError(err?.message || 'Could not reopen the order line.'); }
  };
  const saveEditedOrder = async (order: EnrichedOrder, patch: { vendor: string; poNumber: string; orderDate: string; expectedDate: string; orderQty: number; notes: string }) => {
    if (!isAdmin) throw new Error('Only an admin can edit a placed order.');
    if (!currentProjectId) throw new Error('Select a project first.');
    if (!patch.orderDate || patch.orderQty <= 0) throw new Error('Enter the order date and quantity.');
    await updateDoc(orderRef(order.id), {
      vendor: patch.vendor.trim(),
      poNumber: patch.poNumber.trim(),
      orderDate: patch.orderDate,
      expectedDate: patch.expectedDate,
      orderQty: patch.orderQty,
      notes: patch.notes.trim(),
      updatedAt: serverTimestamp(),
    });
  };
  const saveEditedReceipt = async (order: EnrichedOrder, receipt: Receipt, patch: {
    date: string; qty: number; reference: string; notes: string; boxes?: number; piecesPerBox?: number; areaPerPiece?: number; photo?: { url: string; path: string };
  }) => {
    if (!isAdmin) throw new Error('Only an admin can change a received delivery.');
    if (isReceiptBilled(receipt)) throw new Error('This receipt is already billed. Void or correct the bill first.');
    if (!currentProjectId) throw new Error('Select a project first.');
    if (!patch.date || patch.qty <= 0) throw new Error('Enter the receipt date and quantity.');
    await runTransaction(db, async (tx) => {
      const ref = orderRef(order.id);
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('This order line no longer exists. Close this window and try again.');
      const receipts: Receipt[] = ((snap.data() as any).receipts || []).map((entry: Receipt) => {
        if (entry.id !== receipt.id) return entry;
        const next: Receipt = {
          ...entry,
          date: patch.date,
          qty: patch.qty,
          reference: patch.reference.trim(),
          notes: patch.notes.trim(),
        };
        if (patch.boxes && patch.boxes > 0) {
          next.boxes = patch.boxes;
          next.piecesPerBox = patch.piecesPerBox;
          next.areaPerPiece = patch.areaPerPiece;
        } else {
          delete next.boxes;
          delete next.piecesPerBox;
          delete next.areaPerPiece;
        }
        if (patch.photo) {
          next.photoUrl = patch.photo.url;
          next.photoPath = patch.photo.path;
        }
        return next;
      });
      if (!receipts.some((entry) => entry.id === receipt.id)) throw new Error('This receipt no longer exists. Close this window and try again.');
      tx.update(ref, { receipts, updatedAt: serverTimestamp() });
    });
  };
  const confirmUnplanned = async (order: EnrichedOrder) => {
    if (!isAdmin) { setError('Only an admin can change a received delivery.'); return; }
    const item = materialItems.find((candidate) => candidate.id === order.materialId);
    const wantsQuote = needsVendorQuote(item, order.vendor);
    try {
      await updateDoc(orderRef(order.id), { unplannedReviewed: true, ...(wantsQuote ? { vendorQuotePending: true } : {}), updatedAt: serverTimestamp() });
      if (wantsQuote && item) setQuoteFlow({ order, item, stage: 'ask' });
    } catch (err: any) { setError(err?.message || 'Could not confirm the delivery.'); }
  };
  const rejectUnplanned = async (order: EnrichedOrder) => {
    if (!isAdmin) { setError('Only an admin can change a received delivery.'); return; }
    const note = window.prompt(`Reject "${orderTitle(order)}"? Tell site staff why (optional).`, '');
    if (note === null) return;
    try { await updateDoc(orderRef(order.id), { unplannedRejected: true, unplannedRejectNote: note.trim(), unplannedReviewed: true, updatedAt: serverTimestamp() }); }
    catch (err: any) { setError(err?.message || 'Could not reject the delivery.'); }
  };
  // Moves a no-PO delivery's receipts onto an existing open PO line and removes the placeholder order.
  const linkUnplanned = async (order: EnrichedOrder, targetId: string) => {
    if (!isAdmin) throw new Error('Only an admin can change a received delivery.');
    await runTransaction(db, async (tx) => {
      const sourceRef = orderRef(order.id);
      const targetRef = orderRef(targetId);
      const [source, target] = await Promise.all([tx.get(sourceRef), tx.get(targetRef)]);
      if (!source.exists() || !target.exists()) throw new Error('That order line no longer exists. Close this window and try again.');
      const moved: Receipt[] = ((source.data() as any).receipts || []).map((receipt: Receipt) => ({ ...receipt, id: `${receipt.id}_moved` }));
      tx.update(targetRef, { receipts: [...((target.data() as any).receipts || []), ...moved], updatedAt: serverTimestamp() });
      tx.delete(sourceRef);
    });
  };
  // Links an "Other item" delivery to a library material (the received unit is kept as entered).
  const mapUnplanned = async (order: EnrichedOrder, pick: MaterialPick) => {
    if (!isAdmin) throw new Error('Only an admin can change a received delivery.');
    const wantsQuote = needsVendorQuote(pick.item, order.vendor);
    await updateDoc(orderRef(order.id), {
      materialId: pick.item.id, quoteOptionId: pick.optionId, materialAssemblyType: pick.assemblyType, materialVariety: pick.variety,
      materialLabel: pick.label, needsMapping: false, unplannedReviewed: true, ...(wantsQuote ? { vendorQuotePending: true } : {}), updatedAt: serverTimestamp(),
    });
    if (wantsQuote) setQuoteFlow({ order: { ...order, materialId: pick.item.id, materialLabel: pick.label, quoteOptionId: pick.optionId }, item: pick.item, stage: 'ask' });
  };
  // Adds the delivering vendor's quote to the material's rates and links the waiting deliveries to it.
  // The quote is shared by every project, so any project can use it afterwards.
  const saveVendorQuote = async (flow: { order: EnrichedOrder; item: MaterialRateItem }, quote: MaterialQuote) => {
    if (!currentProjectId) return;
    if (library.mode !== 'shared') throw new Error('The rate library is being moved to the shared library. An admin needs to finish that before quotes can be added here.');
    const projectId = currentProjectId;
    const waitingIds = Array.from(new Set([
      flow.order.id,
      ...enriched.filter((candidate) => candidate.vendorQuotePending && candidate.materialId === flow.item.id && sameVendorName(candidate.vendor, quote.vendor)).map((candidate) => candidate.id),
    ]));
    await runTransaction(db, async (tx) => {
      const [masterSnap, ratesSnap, ...orderSnaps] = await Promise.all([tx.get(masterRef(flow.item.id)), tx.get(projectRatesRef(projectId, flow.item.id)), ...waitingIds.map((id) => tx.get(orderRef(id)))]);
      if (!masterSnap.exists()) throw new Error('This material is no longer in the rate library.');
      const quotes = effectiveQuotes(masterSnap.data() as any, ratesSnap.data() as any) as MaterialQuote[];
      const existing = quotes.find((candidate) => (candidate.status || 'active') !== 'voided' && sameVendorName(candidate.vendor, quote.vendor));
      const quoteId = existing?.id || quote.id;
      if (!existing) {
        const plan = planQuoteWrite([...quotes, { ...quote, status: 'active', recordedAt: new Date().toISOString() }], masterSnap.data() as any, ratesSnap.data() as any);
        tx.update(masterRef(flow.item.id), { quotes: plan.quotes, updatedAt: serverTimestamp() });
        tx.set(projectRatesRef(projectId, flow.item.id), { quoteOverrides: plan.overrides, updatedAt: serverTimestamp() }, { merge: true });
        ensureVendors(projectId, knownVendors, [quote.vendor]).catch(() => undefined);
      }
      orderSnaps.forEach((snap) => { if (snap.exists()) tx.update(snap.ref, { vendorQuotePending: false, newVendor: false, quoteId, updatedAt: serverTimestamp() }); });
    });
  };
  // A material added here goes into the shared library, so every project can order it.
  const createMaterial = async (input: NewMaterialInput) => {
    if (!currentProjectId) throw new Error('Select a project first.');
    if (library.mode !== 'shared') throw new Error('The rate library is being moved to the shared library. An admin needs to finish that before materials can be added.');
    const order = materialItems.reduce((highest, item) => Math.max(highest, Number((item as { order?: number }).order || 0)), 0) + 1;
    return createMaterialItem(currentProjectId, { ...buildMaterialPayload(input, order), createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  };
  // A rate finalised while placing an order: saved to the vendor's quote in the shared library, dated to the order.
  const captureOrderRate = async (input: { materialId: string; optionId: string; unit: string; materialLabel: string; vendor: string; rate: string; gstMode: 'inclusive' | 'exclusive'; orderDate: string; poNumber: string }) => {
    if (!currentProjectId) return;
    const item = materialItems.find((candidate) => candidate.id === input.materialId);
    if (!item) return;
    const reason = input.poNumber ? `Order ${input.poNumber}` : `Order placed ${input.orderDate}`;
    const quotes = planOrderPriceEntry(item, input.optionId, { unit: input.unit, materialLabel: input.materialLabel }, { vendor: input.vendor, rate: input.rate, gstMode: input.gstMode }, input.orderDate, reason);
    if (!quotes) return;
    await setProjectRates(currentProjectId, item.id, { quotes });
    ensureVendors(currentProjectId, knownVendors, [input.vendor]).catch(() => undefined);
  };

  const addVariety = async (item: MaterialRateItem, assemblyType: string, variety: string) => {
    if (!currentProjectId) throw new Error('Select a project first.');
    if (library.mode !== 'shared') throw new Error('The rate library is being moved to the shared library. An admin needs to finish that before materials can be changed.');
    await updateMaterialItem(currentProjectId, item.id, { ...addVarietyPatch(item, assemblyType, variety), updatedAt: serverTimestamp() });
  };
  const dismissVendorQuote = async (orders: EnrichedOrder[]) => {
    try { await Promise.all(orders.map((order) => updateDoc(orderRef(order.id), { vendorQuotePending: false, updatedAt: serverTimestamp() }))); }
    catch (err: any) { setError(err?.message || 'Could not update the reminder.'); }
  };
  const vendorNames = useMemo(() => Array.from(new Set([
    ...materialItems.flatMap((item) => (item.quotes || []).map((quote) => String(quote.vendor || '').trim())),
    ...orders.map((order) => String(order.vendor || '').trim()),
  ].filter(Boolean))).sort((a, b) => a.localeCompare(b)), [materialItems, orders]);
  const brandNames = useMemo(() => Array.from(new Set([
    ...materialItems.flatMap((item) => [item.brand, ...(item.quotes || []).map((quote) => quote.brand)].map((brand) => String(brand || '').trim())),
  ].filter(Boolean))).sort((a, b) => a.localeCompare(b)), [materialItems]);
  const uploadReceiptPhoto = async (file: File) => {
    if (!currentProjectId) throw new Error('Select a project first.');
    const blob = await resizeImage(file);
    const path = `projects/${currentProjectId}/materialReceipts/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
    const fileRef = storageRef(storage, path);
    await uploadBytes(fileRef, blob, { contentType: blob.type || 'image/jpeg' });
    return { url: await getDownloadURL(fileRef), path };
  };
  const photoLink = (receipt: Receipt) => receipt.photoUrl
    ? <Typography component="a" href={receipt.photoUrl} target="_blank" rel="noopener noreferrer" onClick={(event: React.MouseEvent) => event.stopPropagation()} sx={{ fontSize: 11.5, fontWeight: 800, color: GOLD_D }}>Photo</Typography>
    : null;
  const poText = (order: Order) => {
    const po = String(order.poNumber || '').trim();
    if (!po) return 'No PO';
    return po.toLowerCase().startsWith('po') ? po : `PO ${po}`;
  };
  const recentReceiptStatus = (receipt: Receipt) => isReceiptBilled(receipt)
    ? { label: receipt.billNumber ? `Billed · ${receipt.billNumber}` : 'Billed', color: GREEN, bg: '#eaf4ed' }
    : { label: 'Waiting for bill', color: BLUE, bg: '#eef3fb' };
  const receiptQtyText = (order: Order, receipt: Receipt, withReceived = false) => {
    const mainQty = `${fmtQty(receipt.qty)} ${order.unit}`;
    const boxQty = Number(receipt.boxes || 0);
    const physicalQty = boxQty > 0 ? `${fmtQty(boxQty)} Box` : '';
    return `${physicalQty ? `${physicalQty} · ` : ''}${mainQty}${withReceived ? ' received' : ''}`;
  };

  const tabLabel = (key: TabKey) => {
    const count = key === 'orders' ? openOrders.length + unplannedOrders.length : key === 'receiving' ? openOrders.length : key === 'bills' ? unbilledRows.length : 0;
    const name = key === 'orders' ? 'Orders' : key === 'receiving' ? 'Receiving' : key === 'bills' ? 'Bills' : 'Stock';
    return <Stack direction="row" gap={0.7} alignItems="center"><span>{name}</span>{count > 0 && <Chip size="small" label={count} sx={{ height: 18, fontSize: 11, fontWeight: 800, bgcolor: key === 'bills' ? '#eef3fb' : '#fff4e5', color: key === 'bills' ? BLUE : WARN }} />}</Stack>;
  };

  const receiptText = (order: Order, receipt: Receipt) => {
    const doc_ = receipt.documentType === 'bill' ? `Bill ${receipt.reference || receipt.billNumber || ''}` : `Challan ${receipt.reference || 'not added'}`;
    return `${fmtDate(receipt.date)} · ${receiptQtyText(order, receipt, true)} · ${doc_}`;
  };
  const billChip = (receipt: Receipt) => <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: isReceiptBilled(receipt) ? GREEN : BLUE }}>{isReceiptBilled(receipt) ? `Billed${receipt.billNumber ? ` · ${receipt.billNumber}` : ''}` : 'Waiting for bill'}</Typography>;

  const unplannedNode = unplannedOrders.length > 0 ? (
    <Box sx={{ mb: 1.4, border: '1px solid #f0d28d', bgcolor: '#fffaf0', borderRadius: 1.5, overflow: 'hidden' }}>
        <Box sx={{ px: 1.4, py: 0.9 }}><Typography sx={{ fontWeight: 900, fontSize: 13.5, color: GOLD_D }}>Delivered without a PO — please resolve</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>Site staff recorded these deliveries directly. Link one to an open PO, confirm it as a new order, or send it back.</Typography></Box>
        {unplannedOrders.map((order) => {
          const candidates = findOpenPoLines((enriched as unknown as OrderRow[]).filter((candidate) => candidate.id !== order.id), { materialId: order.materialId, quoteOptionId: order.quoteOptionId, materialLabel: order.materialLabel });
          const first = (order.receipts || [])[0];
          return <Stack key={order.id} direction={{ xs: 'column', md: 'row' }} gap={1} alignItems={{ md: 'center' }} sx={{ px: 1.4, py: 0.9, borderTop: '1px solid #f0d28d' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" gap={0.8} alignItems="center" flexWrap="wrap"><Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>{orderTitle(order)}</Typography>{order.needsMapping && <Chip size="small" label="Needs mapping" sx={{ height: 20, bgcolor: '#f6ecdb', color: GOLD_D, fontWeight: 800 }} />}{order.newVendor && <Chip size="small" label="New vendor" sx={{ height: 20, bgcolor: '#e7eff9', color: '#2f5f96', fontWeight: 800 }} />}</Stack>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap"><Typography sx={{ color: MUTED, fontSize: 11.5 }}>{orderGroup(order) ? `${orderGroup(order)} · ` : ''}{order.vendor || 'Vendor not set'} · {first ? receiptQtyText(order, first) : `${fmtQty(order.received)} ${order.unit}`}{first ? ` · ${first.documentType === 'bill' ? 'Bill' : 'Challan'} ${first.reference || ''}` : ''}{order.orderDate ? ` · ${fmtDate(order.orderDate)}` : ''}</Typography>{first && photoLink(first)}</Stack>
              {candidates.length > 0 && <Typography sx={{ color: WARN, fontSize: 11.5, fontWeight: 700 }}>An open PO exists: {candidates.map((candidate) => `${candidate.poNumber || 'PO'} · ${fmtQty(candidate.pending)} ${candidate.unit} pending`).join(' · ')}</Typography>}
            </Box>
            <Stack direction="row" gap={0.6} flexWrap="wrap">
              {isAdmin ? <>
                {candidates.length > 0 && <Button size="small" variant="contained" onClick={() => setResolve({ order, mode: 'link' })}>Link to PO</Button>}
                {order.needsMapping
                  ? <Button size="small" variant="contained" onClick={() => setResolve({ order, mode: 'map' })}>Map to material…</Button>
                  : <Button size="small" variant={candidates.length ? 'outlined' : 'contained'} onClick={() => confirmUnplanned(order)} sx={candidates.length ? { borderColor: GOLD_D, color: GOLD_D } : undefined}>Confirm as is</Button>}
                <Button size="small" color="error" variant="outlined" onClick={() => rejectUnplanned(order)}>Reject</Button>
              </> : <Chip size="small" label="Admin can change" sx={{ bgcolor: '#f6ecdb', color: GOLD_D, fontWeight: 800 }} />}
            </Stack>
          </Stack>;
        })}
      </Box>) : null;
  const quoteNode = vendorQuoteWaiting.length > 0 ? (
    <Box sx={{ mb: 1.4, border: '1px solid #b9cbe6', bgcolor: '#f3f7fc', borderRadius: 1.5, overflow: 'hidden' }}>
        <Box sx={{ px: 1.4, py: 0.9 }}><Typography sx={{ fontWeight: 900, fontSize: 13.5, color: '#2f5f96' }}>Vendors waiting for a rate quote</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>These vendors delivered material but have no quote in the rate library yet. Adding one lets bills start from their rate.</Typography></Box>
        {vendorQuoteWaiting.map((group) => <Stack key={`${group.item.id}_${group.orders[0].id}`} direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }} sx={{ px: 1.4, py: 0.8, borderTop: '1px solid #b9cbe6' }}>
          <Box sx={{ flex: 1 }}><Typography sx={{ fontWeight: 800, fontSize: 13 }}>{group.orders[0].vendor} <Typography component="span" sx={{ color: MUTED, fontSize: 12 }}>· {orderTitle(group.orders[0])}</Typography></Typography></Box>
          <Stack direction="row" gap={0.6}>
            <Button size="small" variant="contained" onClick={() => setQuoteFlow({ order: group.orders[0], item: group.item, stage: 'form' })}>Add quote</Button>
            <Button size="small" onClick={() => dismissVendorQuote(group.orders)} sx={{ color: MUTED }}>Not needed</Button>
          </Stack>
        </Stack>)}
      </Box>) : null;
  const renderOrderReceipts = (order: EnrichedOrder) => (order.receipts || []).length
    ? <Stack gap={0.75}>{[...(order.receipts || [])].sort((a, b) => String(b.date).localeCompare(String(a.date))).map((receipt) => <Box key={receipt.id} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.25, py: 0.9 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{receiptText(order, receipt)}</Typography>
      <Stack direction="row" gap={1.2} alignItems="center">{billChip(receipt)}{photoLink(receipt)}{receipt.notes && <Typography sx={{ fontSize: 11.5, color: FAINT }}>{receipt.notes}</Typography>}</Stack>
    </Box>)}</Stack>
    : <Typography sx={{ color: MUTED, fontSize: 13 }}>Nothing received yet.</Typography>;

  const libraryEmptyMessage = library.mode === 'loading'
    ? 'Loading the rate library…'
    : library.mode === 'shared' && library.sharedCount === 0 && library.legacyItems.length === 0
      ? 'The shared rate library is empty, and this project has no rate library of its own. If another project already has one, open that project, go to Estimation > Rates, and use the yellow panel to move it to the shared library. Otherwise add materials in Estimation > Rates.'
      : undefined;
  const noProject = !currentProjectId;
  const allRecentReceipts = enriched.filter((order) => !order.unplannedRejected).flatMap((order) => (order.receipts || []).map((receipt) => ({ order, receipt })))
    .sort((a, b) => String(b.receipt.date).localeCompare(String(a.receipt.date)) || String(b.receipt.id).localeCompare(String(a.receipt.id)));
  const recentReceiptSearchText = recentReceiptQuery.trim().toLowerCase();
  const filteredRecentReceipts = recentReceiptSearchText
    ? allRecentReceipts.filter(({ order }) => `${orderTitle(order)} ${orderGroup(order)}`.toLowerCase().includes(recentReceiptSearchText))
    : allRecentReceipts;
  const recentReceiptRows = filteredRecentReceipts.slice(0, recentReceiptsShown);
  // Only meaningful once the list is narrowed to (usually) one material — a mixed search just sums by unit.
  const recentReceiptTotalsByUnit = recentReceiptSearchText
    ? filteredRecentReceipts.reduce<Record<string, number>>((totals, { order, receipt }) => {
        const unit = order.unit || '';
        totals[unit] = (totals[unit] || 0) + Number(receipt.qty || 0);
        return totals;
      }, {})
    : {};

  return <Box sx={{ maxWidth: 1180, mx: 'auto', width: '100%', p: { xs: 1.5, md: 3 } }}>
    <Typography sx={{ mb: 0.75, fontSize: 14, fontWeight: 800, color: DARK }}>Material Orders & Receipts</Typography>
    {error && <Alert severity="error" sx={{ mb: 1.2 }} onClose={() => setError('')}>{error}</Alert>}
    {noProject && <Alert severity="info">Select a project from the top bar.</Alert>}
    {availableTabs.length > 1 && <Tabs value={tab} onChange={(_, value) => setTabChoice(value)} sx={{ mb: 1.5, borderBottom: `1px solid ${BORDER}`, '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 44 } }}>
      {availableTabs.map((key) => <Tab key={key} value={key} label={tabLabel(key)} />)}
    </Tabs>}

    {tab === 'orders' && <DialogErrorBoundary key={ordersKey} onClose={() => setOrdersKey((value) => value + 1)}>
      <OrdersScreen
        canManage={canManage}
        canEditPlacedOrders={isAdmin}
        emptyLibraryMessage={libraryEmptyMessage}
        knownVendors={knownVendors}
        onCreateMaterial={canManage ? createMaterial : undefined}
        onAddVariety={canManage ? addVariety : undefined}
        onCaptureRate={canManage ? captureOrderRate : undefined}
        orders={enriched as unknown as OrdersScreenOrder[]}
        materialItems={materialItems}
        requirements={orderRequirements}
        unplannedCount={unplannedOrders.length}
        unplannedPanel={unplannedNode}
        quoteCount={vendorQuoteWaiting.length}
        quotePanel={quoteNode}
        renderReceipts={renderOrderReceipts}
        onPlaceOrders={placeOrders}
        onEditOrder={(order) => setEditingOrder(order as EnrichedOrder)}
        onCloseShort={closeShort}
        onReopen={reopenOrder}
      />
    </DialogErrorBoundary>}

    {/* On a desktop the receiving screen and the recent receipts sit side by side; on a phone they stack. */}
    {tab === 'receiving' && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 440px) minmax(0, 1fr)' }, columnGap: 3, alignItems: 'start' }}>
      <DialogErrorBoundary key={receivingKey} onClose={() => setReceivingKey((value) => value + 1)}>
        <ReceivingScreen orders={enriched as unknown as OrderRow[]} materialItems={materialItems} uploadPhoto={uploadReceiptPhoto} onSavePo={saveDeliveryReceipts} onSaveDirect={saveDirectReceipt} cartingAgentsByVendor={cartingAgentsByVendor} onSaveCartingAgents={(vendorName, agents) => { saveCartingAgents(vendorName, agents).catch(() => undefined); }} />
      </DialogErrorBoundary>

      <Box sx={{ minWidth: 0 }}>
      {rejectedOrders.length > 0 && <Box sx={{ mt: { xs: 2.2, md: 0 }, border: '1px solid #e2b4b4', bgcolor: '#fdf3f3', borderRadius: 1.5, overflow: 'hidden' }}>
        <Box sx={{ px: 1.4, py: 0.9 }}><Typography sx={{ fontWeight: 900, fontSize: 13.5, color: '#a83232' }}>Sent back by purchasing</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>These deliveries were not accepted. Record them again correctly if they are still needed.</Typography></Box>
        {rejectedOrders.map((order) => {
          const first = (order.receipts || [])[0];
          return <Box key={order.id} sx={{ px: 1.4, py: 0.8, borderTop: '1px solid #e2b4b4' }}><Typography sx={{ fontWeight: 800, fontSize: 13 }}>{orderTitle(order)} · {first ? receiptQtyText(order, first) : `${fmtQty(order.received)} ${order.unit}`}</Typography><Typography sx={{ color: MUTED, fontSize: 11.5 }}>{orderGroup(order) ? `${orderGroup(order)} · ` : ''}{order.vendor || 'Vendor not set'}{order.unplannedRejectNote ? ` · ${order.unplannedRejectNote}` : ''}</Typography></Box>;
        })}
      </Box>}

      <Box sx={{ mt: rejectedOrders.length > 0 ? 2.2 : { xs: 2.2, md: 0 }, border: `1px solid ${BORDER}`, borderRadius: 4, overflow: 'hidden', bgcolor: '#fff' }}>
        <Stack direction="row" alignItems="center" sx={{ px: 1.75, py: 1.25, bgcolor: '#faf8f5', borderBottom: `1px solid ${BORDER}` }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: 'Georgia, serif', color: DARK, fontSize: 20, fontWeight: 700 }}>Recent receipts</Typography>
            <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Latest challans and bill status from site entries.</Typography>
          </Box>
          {allRecentReceipts.length > 0 && <Chip size="small" label={allRecentReceipts.length} sx={{ bgcolor: '#f6ecdb', color: GOLD_D, fontWeight: 700 }} />}
        </Stack>
        <Box sx={{ px: 1.5, py: 1.1, borderBottom: `1px solid ${BORDER}` }}>
          <TextField
            size="small" fullWidth value={recentReceiptQuery}
            onChange={(event) => { setRecentReceiptQuery(event.target.value); setRecentReceiptsShown(8); }}
            placeholder="Search by material to see what's been received, and when"
            inputProps={{ 'aria-label': 'Search recent receipts by material' }}
          />
          {recentReceiptSearchText && <Typography sx={{ mt: 0.9, color: DARK, fontSize: 13, fontWeight: 600 }}>
            {filteredRecentReceipts.length} receipt{filteredRecentReceipts.length === 1 ? '' : 's'} · Total received: {Object.keys(recentReceiptTotalsByUnit).length
              ? Object.entries(recentReceiptTotalsByUnit).map(([unit, qty]) => `${fmtQty(qty)}${unit ? ` ${unit}` : ''}`).join(', ')
              : '0'}
          </Typography>}
        </Box>
        <Stack gap={0} sx={{ bgcolor: '#fff' }}>
          {recentReceiptRows.length > 0 && <Box sx={{ display: { xs: 'none', lg: 'grid' }, gridTemplateColumns: 'minmax(0, 1.2fr) minmax(230px, .75fr) minmax(190px, .55fr)', gap: 1.4, px: 1.5, py: 0.75, bgcolor: '#faf8f5', borderBottom: `1px solid ${BORDER}` }}>
            {['Material', 'Receipt', 'Status'].map((label) => <Typography key={label} sx={{ color: FAINT, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.45px' }}>{label}</Typography>)}
          </Box>}
          {recentReceiptRows.map(({ order, receipt }, index) => {
            const status = recentReceiptStatus(receipt);
            return <Box key={`${order.id}_${receipt.id}`} sx={{ px: 1.5, py: 1.15, borderTop: index ? `1px solid ${BORDER}` : 0, '&:hover': { bgcolor: '#fffaf0' } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.2fr) minmax(230px, .75fr) minmax(190px, .55fr)' }, gap: { xs: 0.75, lg: 1.4 }, alignItems: 'center' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: DARK, fontSize: 14.5, fontWeight: 700, overflowWrap: 'anywhere' }}>{orderTitle(order)}</Typography>
                <Stack direction="row" gap={0.7} alignItems="center" flexWrap="wrap" sx={{ mt: 0.35 }}>
                  {orderGroup(order) && <Typography sx={{ color: MUTED, fontSize: 12.5, overflowWrap: 'anywhere' }}>{orderGroup(order)}</Typography>}
                  <Typography sx={{ color: MUTED, fontSize: 12.5, overflowWrap: 'anywhere' }}>{order.vendor || 'Vendor not set'}</Typography>
                  <Chip size="small" label={poText(order)} sx={{ height: 21, bgcolor: '#f6ecdb', color: GOLD_D, fontSize: 11, fontWeight: 600 }} />
                </Stack>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: DARK, fontSize: 13, fontWeight: 500, overflowWrap: 'anywhere' }}>{fmtDate(receipt.date)} · {receiptQtyText(order, receipt)}</Typography>
                <Typography sx={{ color: MUTED, fontSize: 12.5, mt: 0.1, overflowWrap: 'anywhere' }}>{receipt.documentType === 'bill' ? 'Bill' : 'Challan'} {receipt.reference || receipt.billNumber || 'not added'}</Typography>
              </Box>
              <Stack direction="row" gap={1} alignItems="center" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} flexWrap="wrap">
                <Chip size="small" label={status.label} sx={{ height: 24, maxWidth: '100%', bgcolor: status.bg, color: status.color, fontWeight: 600, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                {photoLink(receipt)}
                {isAdmin && !isReceiptBilled(receipt) && <Button size="small" onClick={() => setEditingReceipt({ order, receipt })} sx={{ minWidth: 0, px: 0.75, color: GOLD_D, fontWeight: 600, textTransform: 'none' }}>Edit</Button>}
              </Stack>
            </Box>
            {receipt.notes && <Typography sx={{ color: FAINT, fontSize: 11.5, mt: 0.6 }}>{receipt.notes}</Typography>}
          </Box>;
          })}
          {!recentReceiptRows.length && <Typography sx={{ p: 2, color: MUTED, fontSize: 13 }}>{recentReceiptSearchText ? `No receipts match "${recentReceiptQuery.trim()}".` : 'No deliveries recorded yet.'}</Typography>}
        </Stack>
        {filteredRecentReceipts.length > recentReceiptRows.length && <Box sx={{ p: 1.25, textAlign: 'center', borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
          <Button size="small" onClick={() => setRecentReceiptsShown((previous) => previous + 8)} sx={{ color: GOLD_D, fontWeight: 600, textTransform: 'none' }}>Load more ({filteredRecentReceipts.length - recentReceiptRows.length} more)</Button>
        </Box>}
      </Box>
      </Box>
    </Box>}

    {tab === 'bills' && <DialogErrorBoundary key={billsKey} onClose={() => setBillsKey((value) => value + 1)}>
      <BillsScreen orders={enriched as unknown as OrderRow[]} materialItems={materialItems} bills={bills as BillView[]} onSaveBill={saveBill} onVoidBill={voidBill as (bill: BillView) => void} canDirect={canDirectBill} vendorNames={knownVendors} onSaveDirectBill={saveDirectBill} onLinkBill={linkBill} onMatchBillLine={matchBillLine} onCreateMaterial={canDirectBill ? createMaterial : undefined} />
    </DialogErrorBoundary>}

    {tab === 'stock' && <StockScreen
      materialItems={materialItems}
      orders={enriched}
      issues={stockIssues}
      concreteGrades={concreteGrades}
      onSaveIssues={saveStockIssues}
      onDeleteIssue={deleteStockIssue}
    />}

    {quoteFlow?.stage === 'ask' && <Dialog open onClose={() => setQuoteFlow(null)} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 900 }}>Add {quoteFlow.order.vendor} to the rate library?</DialogTitle>
      <DialogContent><Typography sx={{ color: MUTED, fontSize: 14 }}>{quoteFlow.order.vendor} has no quote for {orderTitle(quoteFlow.order)}. Add one now so bills can start from their rate. You can also save the vendor without a rate.</Typography></DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setQuoteFlow(null)}>Later</Button><Button variant="contained" onClick={() => setQuoteFlow({ ...quoteFlow, stage: 'form' })}>Add quote</Button></DialogActions>
    </Dialog>}
    {quoteFlow?.stage === 'form' && <DialogErrorBoundary onClose={() => setQuoteFlow(null)}><SteelQuoteDialog item={quoteFlow.item} vendorOptions={vendorOptionsForItem(quoteFlow.item, enriched, vendorNames)} brandOptions={brandNames} initialVendor={quoteFlow.order.vendor} initialBrand={quoteFlow.order.brand} allowNoRate cartingAgentsByVendor={cartingAgentsByVendor} onSaveCartingAgents={(vendorName, agents) => { saveCartingAgents(vendorName, agents).catch(() => undefined); }} onClose={() => setQuoteFlow(null)} onSave={async (quote) => { try { await saveVendorQuote(quoteFlow, quote); setQuoteFlow(null); } catch (err: any) { setError(err?.message || 'Could not save the quote.'); setQuoteFlow(null); } }} /></DialogErrorBoundary>}
    {resolve && <DialogErrorBoundary onClose={() => setResolve(null)}><ResolveUnplannedDialog mode={resolve.mode} order={resolve.order} candidates={findOpenPoLines((enriched as unknown as OrderRow[]).filter((candidate) => candidate.id !== resolve.order.id), { materialId: resolve.order.materialId, quoteOptionId: resolve.order.quoteOptionId, materialLabel: resolve.order.materialLabel })} picks={libraryPicks(materialItems)} onClose={() => setResolve(null)} onLink={(targetId) => linkUnplanned(resolve.order, targetId)} onMap={(pick) => mapUnplanned(resolve.order, pick)} /></DialogErrorBoundary>}
    {editingReceipt && <EditReceiptDialog
      order={editingReceipt.order}
      receipt={editingReceipt.receipt}
      materialItems={materialItems}
      uploadPhoto={uploadReceiptPhoto}
      onClose={() => setEditingReceipt(null)}
      onSave={async (patch) => {
        try {
          await saveEditedReceipt(editingReceipt.order, editingReceipt.receipt, patch);
          setEditingReceipt(null);
        } catch (err: any) {
          setError(err?.message || 'Could not edit this receipt.');
          setEditingReceipt(null);
        }
      }}
    />}
    {editingOrder && <EditPlacedOrderDialog
      order={editingOrder}
      vendorOptions={(() => { const item = materialItems.find((candidate) => candidate.id === editingOrder.materialId); return item ? vendorOptionsForItem(item, enriched, knownVendors) : knownVendors; })()}
      onClose={() => setEditingOrder(null)}
      onSave={async (patch) => {
        try {
          await saveEditedOrder(editingOrder, patch);
          setEditingOrder(null);
        } catch (err: any) {
          setError(err?.message || 'Could not edit this order.');
          setEditingOrder(null);
        }
      }}
    />}
  </Box>;
}

function currentBoxPackingFor(order: EnrichedOrder, receipt: Receipt, materialItems: MaterialRateItem[]): MaterialBoxPacking | null {
  const pick = pickForOrder(libraryPicks(materialItems), order as unknown as MaterialProcurementOrder);
  const packings = pick?.item.boxPackings || [];
  if (!packings.length) return null;
  return packings.find((packing) => sameText(packing.assemblyType, pick?.assemblyType) && sameText(packing.variety, pick?.variety))
    || packings.find((packing) => Number(packing.piecesPerBox || 0) === Number(receipt.piecesPerBox || 0))
    || (packings.length === 1 ? packings[0] : null);
}

function EditReceiptDialog({ order, receipt, materialItems, uploadPhoto, onClose, onSave }: {
  order: EnrichedOrder;
  receipt: Receipt;
  materialItems: MaterialRateItem[];
  uploadPhoto: (file: File) => Promise<{ url: string; path: string }>;
  onClose: () => void;
  onSave: (patch: { date: string; qty: number; reference: string; notes: string; boxes?: number; piecesPerBox?: number; areaPerPiece?: number; photo?: { url: string; path: string } }) => Promise<void>;
}) {
  const [date, setDate] = useState(receipt.date || new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState(receipt.reference || receipt.billNumber || '');
  const [notes, setNotes] = useState(receipt.notes || '');
  const [qty, setQty] = useState(String(receipt.qty || ''));
  const [boxes, setBoxes] = useState(String(receipt.boxes || ''));
  const [piecesPerBox, setPiecesPerBox] = useState(String(receipt.piecesPerBox || ''));
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');
  const currentPacking = useMemo(() => currentBoxPackingFor(order, receipt, materialItems), [order, receipt, materialItems]);
  const areaPerPiece = toNumber(currentPacking?.areaPerPiece || receipt.areaPerPiece);
  const isBoxReceipt = Number(receipt.boxes || 0) > 0 && areaPerPiece > 0;
  const boxQty = toNumber(boxes);
  const pieces = toNumber(piecesPerBox);
  const calculatedQty = isBoxReceipt ? boxQty * pieces * areaPerPiece : toNumber(qty);
  const invalid = !date || calculatedQty <= 0 || (isBoxReceipt && (boxQty <= 0 || pieces <= 0));

  useEffect(() => {
    if (!photo) { setPreview(''); return; }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const save = async () => {
    if (invalid) return;
    setSaving(true);
    setLocalError('');
    try {
      let uploaded: { url: string; path: string } | undefined;
      if (photo) uploaded = await uploadPhoto(photo);
      await onSave({
        date,
        qty: calculatedQty,
        reference,
        notes,
        ...(isBoxReceipt ? { boxes: boxQty, piecesPerBox: pieces, areaPerPiece } : {}),
        ...(uploaded ? { photo: uploaded } : {}),
      });
    } catch (err: any) {
      setLocalError(err?.message || 'Could not save receipt.');
    } finally {
      setSaving(false);
    }
  };

  return <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 900 }}>Edit received material</DialogTitle>
    <DialogContent>
      <Stack gap={1.4} sx={{ pt: 1 }}>
        {localError && <Alert severity="error">{localError}</Alert>}
        <Box sx={{ px: 1.3, py: 1.05, bgcolor: '#faf8f5', border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
          <Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Material</Typography>
          <Typography sx={{ color: DARK, fontSize: 16, fontWeight: 800 }}>{orderTitle(order)}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>{order.vendor || 'Vendor not set'} · {receipt.documentType === 'bill' ? 'Bill' : 'Challan'} {receipt.reference || receipt.billNumber || 'not added'}</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <TextField label="Receipt date" type="date" value={date} onChange={(event) => setDate(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label={receipt.documentType === 'bill' ? 'Bill number' : 'Challan number'} value={reference} onChange={(event) => setReference(event.target.value)} fullWidth />
        </Stack>
        {isBoxReceipt ? <>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <TextField label="Boxes received" value={boxes} onChange={(event) => setBoxes(event.target.value)} inputProps={{ inputMode: 'decimal' }} error={boxQty <= 0} fullWidth />
            <TextField label="Tiles / box" value={piecesPerBox} onChange={(event) => setPiecesPerBox(event.target.value)} inputProps={{ inputMode: 'decimal' }} error={pieces <= 0} fullWidth />
          </Stack>
          <Box sx={{ px: 1.2, py: 0.9, bgcolor: '#fffaf0', borderRadius: 1.5, border: '1px solid #ead7ba' }}>
            <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Calculated main quantity</Typography>
            <Typography sx={{ color: DARK, fontWeight: 900 }}>{fmtQty(calculatedQty)} {order.unit}</Typography>
            <Typography sx={{ color: FAINT, fontSize: 11.5 }}>{fmtQty(areaPerPiece)} {order.unit} per tile</Typography>
          </Box>
        </> : <TextField label={`Received quantity (${order.unit})`} value={qty} onChange={(event) => setQty(event.target.value)} inputProps={{ inputMode: 'decimal' }} error={toNumber(qty) <= 0} fullWidth />}
        <TextField label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={2} fullWidth />
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }}>
          {(preview || receipt.photoUrl) && <Box component="img" src={preview || receipt.photoUrl} alt="Receipt photo" sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5, border: `1px solid ${BORDER}` }} />}
          <Button variant="outlined" component="label" disabled={saving} sx={{ borderColor: GOLD_D, color: GOLD_D, textTransform: 'none', fontWeight: 800 }}>
            {receipt.photoUrl || photo ? 'Replace photo' : 'Add photo'}
            <input type="file" accept="image/*" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) setPhoto(file); event.target.value = ''; }} />
          </Button>
          {photo && <Typography sx={{ color: MUTED, fontSize: 12 }}>{photo.name}</Typography>}
        </Stack>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} disabled={saving} sx={{ color: MUTED }}>Cancel</Button>
      <Button variant="contained" onClick={save} disabled={saving || invalid}>{saving ? 'Saving…' : 'Save changes'}</Button>
    </DialogActions>
  </Dialog>;
}

function EditPlacedOrderDialog({ order, vendorOptions, onClose, onSave }: {
  order: EnrichedOrder;
  vendorOptions: string[];
  onClose: () => void;
  onSave: (patch: { vendor: string; poNumber: string; orderDate: string; expectedDate: string; orderQty: number; notes: string }) => Promise<void>;
}) {
  const [vendor, setVendor] = useState(order.vendor || '');
  const [poNumber, setPoNumber] = useState(order.poNumber || '');
  const [orderDate, setOrderDate] = useState(order.orderDate || new Date().toISOString().slice(0, 10));
  const [expectedDate, setExpectedDate] = useState(order.expectedDate || '');
  const [orderQty, setOrderQty] = useState(String(order.orderQty || ''));
  const [notes, setNotes] = useState(order.notes || '');
  const [saving, setSaving] = useState(false);
  const qty = Number(orderQty);
  const invalidQty = !Number.isFinite(qty) || qty <= 0;
  const received = order.received || 0;
  const save = async () => {
    if (!orderDate || invalidQty) return;
    setSaving(true);
    try { await onSave({ vendor, poNumber, orderDate, expectedDate, orderQty: qty, notes }); }
    finally { setSaving(false); }
  };
  return <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 900 }}>Edit placed order</DialogTitle>
    <DialogContent>
      <Stack gap={1.4} sx={{ pt: 1 }}>
        <Box sx={{ px: 1.3, py: 1.05, bgcolor: '#faf8f5', border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
          <Typography sx={{ color: FAINT, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Material</Typography>
          <Typography sx={{ color: DARK, fontSize: 16, fontWeight: 800 }}>{orderTitle(order)}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>{fmtQty(received)} {order.unit} already received</Typography>
        </Box>
        <Autocomplete
          freeSolo
          options={vendorOptions}
          value={vendor}
          onInputChange={(_, value) => setVendor(value)}
          onChange={(_, value) => setVendor(typeof value === 'string' ? value : value || '')}
          renderInput={(params) => <TextField {...params} label="Vendor" inputProps={{ ...params.inputProps, 'aria-label': 'Vendor' }} />}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <TextField label={`Order qty (${order.unit})`} type="number" value={orderQty} onChange={(event) => setOrderQty(event.target.value)} error={invalidQty} helperText={invalidQty ? 'Enter a valid quantity.' : qty < received ? `${fmtQty(received - qty)} ${order.unit} already received above this order quantity.` : ''} inputProps={{ min: 0, step: 'any' }} fullWidth />
          <TextField label="PO / reference no." value={poNumber} onChange={(event) => setPoNumber(event.target.value)} fullWidth />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <TextField label="Order date" type="date" value={orderDate} onChange={(event) => setOrderDate(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="Expected delivery" type="date" value={expectedDate} onChange={(event) => setExpectedDate(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
        <TextField label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} multiline minRows={2} fullWidth />
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} disabled={saving} sx={{ color: MUTED }}>Cancel</Button>
      <Button variant="contained" onClick={save} disabled={saving || !orderDate || invalidQty}>{saving ? 'Saving…' : 'Save changes'}</Button>
    </DialogActions>
  </Dialog>;
}

function ResolveUnplannedDialog({ mode, order, candidates, picks, onClose, onLink, onMap }: {
  mode: 'link' | 'map';
  order: EnrichedOrder;
  candidates: OrderRow[];
  picks: MaterialPick[];
  onClose: () => void;
  onLink: (targetId: string) => Promise<void>;
  onMap: (pick: MaterialPick) => Promise<void>;
}) {
  const [targetId, setTargetId] = useState(candidates[0]?.id || '');
  const [pick, setPick] = useState<MaterialPick | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try {
      if (mode === 'link') await onLink(targetId); else if (pick) await onMap(pick);
      onClose();
    } catch (err: any) { setError(err?.message || 'Could not save.'); } finally { setSaving(false); }
  };
  return <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 900 }}>{mode === 'link' ? 'Link to an open PO' : 'Map to a library material'}</DialogTitle>
    <DialogContent dividers><Stack gap={1.25}>
      <Typography sx={{ color: MUTED, fontSize: 13 }}>{orderTitle(order)} · {order.vendor || 'Vendor not set'} · {fmtQty(order.received)} {order.unit}</Typography>
      {mode === 'link'
        ? <RadioGroup value={targetId} onChange={(event) => setTargetId(event.target.value)}>{candidates.map((candidate) => <FormControlLabel key={candidate.id} value={candidate.id} control={<Radio />} label={`${candidate.vendor?.trim() || 'Vendor not set'} · ${candidate.poNumber || 'PO'} · ${fmtQty(candidate.pending)} ${candidate.unit} pending`} />)}</RadioGroup>
        : <Autocomplete options={picks} value={pick} onChange={(_, value) => setPick(value)} getOptionLabel={(entry) => entry.label} isOptionEqualToValue={(a, b) => a.key === b.key} renderInput={(params) => <TextField {...params} label="Library material" placeholder="Group, assembly or variety" />} />}
      {mode === 'link' && <Typography sx={{ color: MUTED, fontSize: 12 }}>The receipt moves onto that PO line and this placeholder order is removed.</Typography>}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack></DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} disabled={saving}>Cancel</Button>
      <Button variant="contained" onClick={save} disabled={saving || (mode === 'link' ? !targetId : !pick)}>{saving ? 'Saving...' : mode === 'link' ? 'Link receipt' : 'Map material'}</Button>
    </DialogActions>
  </Dialog>;
}
